import React from 'react';
import { Input, Form } from 'antd';
import type { ComponentConfig, ValueChangeEvent } from '../types';
import { getTextFieldOptions } from '../types';
import { isTransformNameUnique } from '../../../utils/validationUtils';
import { useStore } from '../../../store/store';

const { TextArea } = Input;

interface TextFieldProps {
  config: ComponentConfig;
  value?: string;
  onChange: (event: ValueChangeEvent) => void;
  pageId: string;
  error?: string[];
}

export const TextField: React.FC<TextFieldProps> = ({
  config,
  value,
  onChange,
  pageId,
  error
}) => {
  // Get current node ID for unique validation
  const selectedNodeId = useStore(state => {
    const activePipeline = state.pipelines.find(p => p.id === state.activePipelineId);
    return activePipeline?.selectedNodeId;
  });

  // Extract text field specific options from additionalOptions
  const textOptions = getTextFieldOptions(config);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let newValue = e.target.value;
    
    // Apply format mask if provided
    if (textOptions.formatMask && newValue) {
      newValue = applyFormatMask(newValue, textOptions.formatMask);
    }
    
    onChange({
      componentId: config.id,
      value: newValue,
      pageId
    });
  };

  const validateRules = () => {
    const rules: any[] = [];
    
    if (config.required) {
      rules.push({
        required: true,
        message: `${config.label} is required`
      });
    }
    
    if (textOptions.minLength) {
      rules.push({
        min: textOptions.minLength,
        message: `${config.label} must be at least ${textOptions.minLength} characters`
      });
    }
    
    if (textOptions.maxLength) {
      rules.push({
        max: textOptions.maxLength,
        message: `${config.label} must not exceed ${textOptions.maxLength} characters`
      });
    }
    
    // Add custom validation rules
    config.validation?.forEach(rule => {
      switch (rule.type) {
        case 'pattern':
          rules.push({
            pattern: new RegExp(rule.value),
            message: rule.message
          });
          break;
        case 'uniqueInPipeline':
          rules.push({
            validator: async (_: any, value: any) => {
              if (!value || value.trim() === '') {
                return Promise.resolve(); // Empty values handled by required validation
              }
              
              const isUnique = isTransformNameUnique(value, selectedNodeId || undefined);
              if (!isUnique) {
                return Promise.reject(new Error(rule.message || 'This name is already used by another transform'));
              }
              return Promise.resolve();
            }
          });
          break;
        // Add more validation types as needed
      }
    });
    
    return rules;
  };

  const inputProps = {
    placeholder: textOptions.placeholder,
    disabled: config.disabled,
    maxLength: textOptions.maxLength,
    value: value || config.defaultValue || '',
    onChange: handleChange,
    style: { width: '100%' },
    type: textOptions.type || 'text',
    min: textOptions.min,
    max: textOptions.max,
    step: textOptions.step
  };

  const formItemProps = {
    label: config.label,
    required: config.required,
    validateStatus: error && error.length > 0 ? 'error' as const : undefined,
    help: error && error.length > 0 ? error[0] : config.description,
    rules: validateRules(),
    labelCol: { span: 24 },
    wrapperCol: { span: 24 }
  };

  return (
    <Form.Item {...formItemProps}>
      {textOptions.multiline ? (
        <TextArea
          {...inputProps}
          rows={textOptions.rows || 3}
          autoSize={textOptions.rows ? false : { minRows: 2, maxRows: 6 }}
        />
      ) : (
        <Input {...inputProps} />
      )}
    </Form.Item>
  );
};

// Helper function to apply format masks
const applyFormatMask = (value: string, mask: string): string => {
  // Remove all non-alphanumeric characters from value
  const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '');
  let formattedValue = '';
  let valueIndex = 0;
  
  for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i++) {
    const maskChar = mask[i];
    
    if (maskChar === '#') {
      // # represents a digit placeholder
      if (/\d/.test(cleanValue[valueIndex])) {
        formattedValue += cleanValue[valueIndex];
        valueIndex++;
      } else {
        break;
      }
    } else if (maskChar === 'A') {
      // A represents a letter placeholder
      if (/[a-zA-Z]/.test(cleanValue[valueIndex])) {
        formattedValue += cleanValue[valueIndex];
        valueIndex++;
      } else {
        break;
      }
    } else if (maskChar === '*') {
      // * represents any character placeholder
      formattedValue += cleanValue[valueIndex];
      valueIndex++;
    } else {
      // Literal character in mask
      formattedValue += maskChar;
    }
  }
  
  return formattedValue;
}; 